import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Package,
  FileBarChart,
  Warehouse,
  Globe,
  FileText,
  Image,
  Rocket,
  Users,
  RefreshCw,
  Palette,
  FolderTree,
  Brain,
  AlertTriangle,
  ClipboardList,
  Menu,
  X,
  ChevronRight,
  Settings,
  Bot,
  DollarSign,
  LogOut,
  ChevronDown,
  Upload,
  LucideIcon,
} from 'lucide-react';
import { Badge } from './ui/badge';

interface NavItem {
  icon: LucideIcon;
  label: string;
  path?: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  spinning?: boolean;
  pulsing?: boolean;
  badge?: number;
  roles?: string[];
}

interface NavGroup {
  group: string;
  items: NavItem[];
  roles?: string[];
  collapsible?: boolean;
  adminDashboardOnly?: boolean;
}

interface AppLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle: string;
  syncing?: boolean;
  aiScanning?: boolean;
  aiStats?: { inconsistentProducts: number };
  onTicimaxSync?: () => void;
  onOpenColorModal?: () => void;
  onAIScan?: (forceRescan?: boolean) => void;
  onAIScanUncontrolled?: () => void;
  onModelCodeUpload?: () => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  title,
  subtitle,
  syncing = false,
  aiScanning = false,
  aiStats = { inconsistentProducts: 0 },
  onTicimaxSync,
  onOpenColorModal,
  onAIScan,
  onAIScanUncontrolled,
  onModelCodeUpload,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (group: string) => {
    setCollapsedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navGroups: NavGroup[] = [
    {
      group: 'Ana Sayfalar',
      items: [
        {
          icon: LayoutDashboard,
          label: 'Admin Panel',
          path: '/dashboard',
          roles: ['admin'],
        },
        {
          icon: Package,
          label: 'Ürün Girişi',
          path: '/production',
          roles: ['admin', 'production'],
        },
        {
          icon: FileBarChart,
          label: 'Nebim İşlemleri',
          path: '/nebim',
          roles: ['admin', 'nebim'],
        },
        {
          icon: Warehouse,
          label: 'Depo Paneli',
          path: '/warehouse',
          roles: ['admin', 'warehouse'],
        },
        {
          icon: Globe,
          label: 'ERP / Ticimax',
          path: '/erp',
          roles: ['admin', 'erp'],
        },
        {
          icon: FileText,
          label: 'İçerik Paneli',
          path: '/content',
          roles: ['admin', 'content'],
        },
        {
          icon: Image,
          label: 'Görsel Paneli',
          path: '/visual',
          roles: ['admin', 'visual'],
        },
        {
          icon: Rocket,
          label: 'Yayınlama',
          path: '/publish',
          roles: ['admin', 'publish'],
        },
        {
          icon: DollarSign,
          label: 'Fiyat Girişi',
          path: '/prices',
          roles: ['admin', 'production', 'nebim'],
        },
      ],
    },
    {
      group: 'Yönetim',
      roles: ['admin'],
      collapsible: true,
      adminDashboardOnly: true,
      items: [
        {
          icon: Users,
          label: 'Kullanıcı Yönetimi',
          path: '/users',
        },
        {
          icon: RefreshCw,
          label: syncing ? 'Çekiliyor...' : 'Ticimax Ürün Getir',
          onClick: onTicimaxSync,
          disabled: syncing,
          spinning: syncing,
        },
        {
          icon: Palette,
          label: 'Renk Yönetimi',
          onClick: onOpenColorModal,
        },
        {
          icon: FolderTree,
          label: 'Kategori Yönetimi',
          path: '/categories',
        },
        {
          icon: FolderTree,
          label: 'Ticimax Kategorileri',
          path: '/ticimax-categories',
        },
        {
          icon: Upload,
          label: 'Model Kodu Yükle (Excel)',
          onClick: onModelCodeUpload,
        },
      ],
    },
    {
      group: 'AI Analiz',
      roles: ['admin'],
      collapsible: true,
      adminDashboardOnly: true,
      items: [
        {
          icon: Brain,
          label: aiScanning ? 'Taranıyor...' : 'Yeni Ürünleri Tara',
          onClick: () => onAIScan?.(false),
          disabled: aiScanning,
          pulsing: aiScanning,
        },
        {
          icon: AlertTriangle,
          label: aiScanning ? 'Taranıyor...' : 'Kontrol Edilmemişleri Tara',
          onClick: () => onAIScanUncontrolled?.(),
          disabled: aiScanning || !onAIScanUncontrolled,
          pulsing: aiScanning,
        },
        {
          icon: RefreshCw,
          label: aiScanning ? 'Taranıyor...' : 'Tümünü Yeniden Tara',
          onClick: () => onAIScan?.(true),
          disabled: aiScanning,
          spinning: aiScanning,
        },
        {
          icon: AlertTriangle,
          label: 'Tutarsız Ürünler',
          path: '/ticimax-content?filter=ai-tutarsiz',
          badge: aiStats.inconsistentProducts,
        },
        {
          icon: Bot,
          label: 'Özel AI Tarama',
          path: '/admin/custom-scan',
        },
      ],
    },
    {
      group: 'Genel',
      items: [
        {
          icon: ClipboardList,
          label: 'Kampanya Checklist',
          path: '/campaign-checklist',
        },
      ],
    },
  ];

  const isAdminDashboard = location.pathname === '/dashboard';

  const filteredGroups = navGroups
    .filter(group => {
      if (group.adminDashboardOnly && !isAdminDashboard) return false;
      if (!group.roles) return true;
      return group.roles.includes(user?.role || '');
    })
    .map(group => ({
      ...group,
      items: group.items.filter(item => {
        if (!item.roles) return true;
        return item.roles.includes(user?.role || '');
      }),
    }))
    .filter(group => group.items.length > 0);

  const isActive = (path?: string) => {
    if (!path) return false;
    const cleanPath = path.split('?')[0];
    return location.pathname === cleanPath;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-white border-r border-gray-200 shadow-sm z-40 transition-all duration-300 ease-in-out flex flex-col ${
          sidebarOpen ? 'w-64' : 'w-0 -translate-x-full'
        } overflow-hidden`}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex-shrink-0">
              <Settings className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold text-gray-800 text-sm truncate">Beauty Omelette</h2>
              <p className="text-xs text-gray-500 truncate">Yönetim Paneli</p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="ml-auto p-1 hover:bg-gray-200 rounded-md transition-colors flex-shrink-0"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Nav Items */}
        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {filteredGroups.map((group, groupIndex) => {
            const isCollapsed = collapsedGroups[group.group];
            return (
              <div key={groupIndex}>
                <button
                  onClick={() => group.collapsible && toggleGroup(group.group)}
                  className={`flex items-center justify-between w-full text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5 px-2 py-1 rounded ${
                    group.collapsible ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-default'
                  }`}
                >
                  <span>{group.group}</span>
                  {group.collapsible && (
                    <ChevronDown
                      className={`h-3 w-3 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                    />
                  )}
                </button>
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {group.items.map((item, itemIndex) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);
                      return (
                        <button
                          key={itemIndex}
                          onClick={() => {
                            if (item.path) {
                              navigate(item.path);
                            } else if (item.onClick) {
                              item.onClick();
                            }
                          }}
                          disabled={item.disabled}
                          className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                            active
                              ? 'bg-primary text-primary-foreground font-medium shadow-sm'
                              : 'text-gray-700 hover:bg-gray-100 font-normal'
                          } ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Icon
                              className={`h-4 w-4 flex-shrink-0 ${
                                item.spinning ? 'animate-spin' : ''
                              } ${item.pulsing ? 'animate-pulse' : ''}`}
                            />
                            <span className="truncate">{item.label}</span>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {item.badge !== undefined && item.badge > 0 && (
                              <Badge variant="destructive" className="text-xs px-1.5 py-0">
                                {item.badge}
                              </Badge>
                            )}
                            {item.path && (
                              <ChevronRight className="h-3 w-3 text-gray-400" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* User Info & Logout */}
        <div className="p-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-semibold flex-shrink-0">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name || 'User'}</div>
              <div className="text-xs text-muted-foreground capitalize">{user?.role || 'user'}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>Oturumu Kapat</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center h-14 px-4 gap-3">
            {!sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Menu className="h-5 w-5 text-gray-600" />
              </button>
            )}
            <div className="min-w-0">
              <h1 className="text-lg font-semibold truncate">{title}</h1>
              <p className="text-xs text-muted-foreground truncate -mt-0.5">{subtitle}</p>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main>
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AppLayout;
